import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import {
    FaHandsHelping,
    FaHeart,
    FaGraduationCap,
    FaHome,
    FaUtensils,
    FaArrowRight
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import main from "../assets/LandingNgo1.jpg";
import '../css/landing.css';

export default function Landing() {
    const navigate = useNavigate();
    const location = useLocation();
    const isHome = location.pathname === '/';

    return (
        <section className="modern-landing-section py-5">
            <Container>
                {/* Bento Section Header */}
                <div className="section-header-bento text-center mb-5">
                    <span className="bento-badge">
                        <FaHeart className="me-2 text-danger" /> Compassion in Action
                    </span>
                    <h2 className="bento-title mt-2">
                        Transforming Lives From <span className="highlight-gold-gradient">Vulnerability to Victory</span>
                    </h2>
                    <p className="bento-subtitle mx-auto">
                        We provide street children, orphans, and distressed youth with the family, nutrition, and education they deserve.
                    </p>
                </div>

                {/* Modern Bento Grid */}
                <div className="bento-grid-wrapper mb-5">
                    <Row className="g-4 align-items-stretch">
                        {/* Main Visual Showcase Card */}
                        <Col lg={7}>
                            <div className="bento-hero-card h-100">
                                <div className="bento-image-container">
                                    <img
                                        src={main}
                                        alt="Loving Arms Children with Founder"
                                        className="bento-main-img"
                                    />
                                    <div className="bento-image-overlay"></div>
                                    <div className="bento-floating-tag">
                                        <div className="pulse-dot-gold"></div>
                                        <span>15+ Years Empowering Youth in AP</span>
                                    </div>
                                    <div className="bento-caption-box">
                                        <h4>"Every child is worthy of love, dignity, and a future."</h4>
                                        <p>— J. Venkata Srikanth, Founder</p>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* Interactive Feature Pillars */}
                        <Col lg={5}>
                            <div className="d-flex flex-column gap-3 h-100 justify-content-between">
                                {/* Pillar 1 */}
                                <div className="bento-feature-tile">
                                    <div className="tile-icon-bubble bg-gold-soft">
                                        <FaHome />
                                    </div>
                                    <div className="tile-content">
                                        <h5>Safe Haven & 24/7 Shelter</h5>
                                        <p>Clean, secure, and family-like living conditions where kids grow up protected.</p>
                                    </div>
                                </div>

                                {/* Pillar 2 */}
                                <div className="bento-feature-tile">
                                    <div className="tile-icon-bubble bg-green-soft">
                                        <FaUtensils />
                                    </div>
                                    <div className="tile-content">
                                        <h5>Wholesome Daily Nutrition</h5>
                                        <p>Three fresh, healthy meals daily plus milk and nutrition supplements.</p>
                                    </div>
                                </div>

                                {/* Pillar 3 */}
                                <div className="bento-feature-tile">
                                    <div className="tile-icon-bubble bg-blue-soft">
                                        <FaGraduationCap />
                                    </div>
                                    <div className="tile-content">
                                        <h5>English Medium Schooling</h5>
                                        <p>Tuition fees, uniforms, study materials, and after-school guidance.</p>
                                    </div>
                                </div>

                                {isHome && (
                                    <div className="bento-cta-tile">
                                        <button
                                            onClick={() => navigate("/about")}
                                            className="btn-bento-action w-100"
                                        >
                                            Read Our Full Journey <FaArrowRight className="ms-2" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Extended Interactive Timeline on /about page */}
                {!isHome && (
                    <div className="timeline-story-section py-5 mt-4">
                        <div className="text-center mb-5">
                            <span className="bento-badge">Our Journey Through Time</span>
                            <h3 className="h2 fw-bold text-dark mt-2">The Story of Loving Arms Orphanage</h3>
                            <p className="text-muted max-w-600 mx-auto">
                                How a heartfelt calling in 1991 transformed into a lifelong sanctuary for street children.
                            </p>
                        </div>

                        <div className="story-timeline-container">
                            {/* Step 1 */}
                            <div className="timeline-step">
                                <div className="timeline-marker">
                                    <span>1991</span>
                                </div>
                                <div className="timeline-card">
                                    <span className="step-tag">The Awakening</span>
                                    <h4>Witnessing Children on the Streets of Hyderabad</h4>
                                    <p>
                                        Between 1991 and 2004, our founder J. Venkata Srikanth observed desperate children working long hours in tea stalls and brick kilns without clothing, food, or identity. Helpless but moved by deep compassion, he resolved to dedicate his life to their protection.
                                    </p>
                                </div>
                            </div>

                            {/* Step 2 */}
                            <div className="timeline-step">
                                <div className="timeline-marker">
                                    <span>2009</span>
                                </div>
                                <div className="timeline-card">
                                    <span className="step-tag">Official Inception</span>
                                    <h4>Establishment of Loving Arms Orphanage</h4>
                                    <p>
                                        In May 2009, with unwavering faith and local community support, the organization was formally registered under the Indian Trust Act of 1882 (#115/IV 2009) to provide free shelter, nutrition, and schooling.
                                    </p>
                                </div>
                            </div>

                            {/* Step 3 */}
                            <div className="timeline-step">
                                <div className="timeline-marker">
                                    <span>Present</span>
                                </div>
                                <div className="timeline-card active-card">
                                    <span className="step-tag tag-gold">Permanent Expansion</span>
                                    <h4>Relocation to Rajampeta & Permanent Construction</h4>
                                    <p>
                                        To serve underserved rural areas, the trust acquired permanent land in Rajampeta Town, YSR Kadapa District. We are currently raising the roof and building community halls for our children.
                                    </p>
                                    <div className="mt-3">
                                        <button
                                            onClick={() => navigate("/construction")}
                                            className="btn-modern-primary"
                                        >
                                            <FaHandsHelping className="me-2" /> Inspect Construction Site
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Container>
        </section>
    );
}